import { api, unwrap } from '../../lib/api'
import type { Schemas } from '../../lib/api'
import type { Database } from '../../lib/database.types'
import { supabase } from '../../lib/supabase'
import { log } from '../../lib/telemetry'

/** A message of the chat. */
export type Message = Schemas['Message']
/** Author of a message; `null` on the message once the account is deleted. */
export type Author = Schemas['Author']
/** A page of messages, oldest first. */
export type MessagePage = Schemas['MessagePage']
/** A `messages` row, as Realtime sends it. */
export type MessageRow = Database['public']['Tables']['messages']['Row']

/**
 * The latest messages (served from Redis by the API).
 * @returns The first page.
 */
export function fetchLatest(): Promise<MessagePage> {
  return unwrap(api.GET('/messages'))
}

/**
 * The page before a message, to scroll back.
 * @param before - Id of the oldest message shown.
 * @returns Older messages.
 */
export function fetchBefore(before: number): Promise<MessagePage> {
  return unwrap(api.GET('/messages', { params: { query: { before } } }))
}

/**
 * Messages after a given one, to catch up after a disconnection.
 * @param after - Id of the newest message received.
 * @returns Newer messages, up to 100.
 */
export function fetchAfter(after: number): Promise<MessagePage> {
  return unwrap(api.GET('/messages', { params: { query: { after } } }))
}

/**
 * Sends a message.
 * @param content - 1 to 144 characters of plain text.
 * @returns The stored message.
 * @throws {ApiError} `banned`, `rate_limited` or `invalid_content`.
 */
export async function sendMessage(content: string): Promise<Message> {
  const message = await unwrap(api.POST('/messages', { body: { content } }))
  log('message_sent')
  return message
}

/**
 * Reports a message to the moderators.
 * @param messageId - The message.
 * @param reason - Why, 1 to 500 characters.
 */
export async function reportMessage(messageId: number, reason: string): Promise<void> {
  await unwrap(api.POST('/reports', { body: { message_id: messageId, reason } }))
}

/**
 * Deletes a message (moderators).
 * @param id - The message.
 */
export async function deleteMessage(id: number): Promise<void> {
  await unwrap(api.DELETE('/messages/{id}', { params: { path: { id } } }))
}

/**
 * Bans a user (moderators).
 * @param userId - The user.
 * @param reason - Shown to them.
 * @param until - End of the ban, or `null` for good.
 */
export async function banUser(userId: string, reason: string, until: Date | null): Promise<void> {
  await unwrap(
    api.POST('/users/{id}/ban', {
      params: { path: { id: userId } },
      body: { reason, until: until?.toISOString() ?? null },
    }),
  )
}

/**
 * Reads the public part of a profile, for a message received through Realtime.
 * @param userId - Author of the message.
 * @returns The author, or `null` if the profile no longer exists.
 */
export async function fetchAuthor(userId: string): Promise<Author | null> {
  const { data, error } = await supabase.from('profiles').select('id, username, role').eq('id', userId).maybeSingle()
  if (error) throw error
  return data ? { id: data.id, username: data.username, moderator: data.role === 'moderator' } : null
}

/** What {@link subscribeToMessages} reports. */
export interface MessageEvents {
  /** A message was written. */
  onInsert: (row: MessageRow) => void
  /** A message was deleted by a moderator or by the retention job. */
  onDelete: (id: number) => void
  /** The channel connected (`SUBSCRIBED`) or lost its connection. */
  onStatus: (status: string) => void
}

/**
 * Listens to inserts and deletes on `messages` (Postgres Changes, filtered by RLS).
 * @param events - Callbacks.
 * @returns A function that stops listening.
 */
export function subscribeToMessages(events: MessageEvents): () => void {
  const channel = supabase
    .channel('messages')
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, (payload) =>
      events.onInsert(payload.new as MessageRow),
    )
    .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'messages' }, (payload) => {
      const id = (payload.old as Partial<MessageRow>).id
      if (id !== undefined) events.onDelete(id)
    })
    .subscribe((status) => {
      if (status !== 'SUBSCRIBED') log('realtime_status', { status })
      events.onStatus(status)
    })
  return () => void supabase.removeChannel(channel)
}
